import type { InventoryItemRow } from "~/@types/inventoryItemRow";
import type { CreateInventoryItemCommandInput } from "~/gql/graphqlBase.generated";
import type { ReasonSelection } from "~/helpers/mobile/stickyReason";
import type { MutationStageOutcome } from "~/helpers/mutationOutcome";

import isRecord from "~/helpers/isRecord";
import isInventoryReconcileAttempt from "~fulfill/inventory/stock/helpers/isInventoryReconcileAttempt";

export interface InventoryReconcileAttempt {
	attemptId?: string;
	input: CreateInventoryItemCommandInput;
	itemName: string;
	locationName: string;
	receipt?: MutationStageOutcome;
}

export interface InventoryReconcileDraft {
	addedLineIds: Record<string, string>;
	batchCodes: Record<string, null | string>;
	grade: string;
	hardgoodsLocationIds: Record<string, string>;
	location: null | { id: string; name: string };
	reason: null | ReasonSelection;
	reasonEdited: boolean;
	rows: InventoryItemRow[];
	unconfirmedAttempts: Record<string, InventoryReconcileAttempt>;
	why: string;
}

const isStringMap = (value: unknown, allowNull = false): boolean =>
	isRecord(value) && Object.values(value).every((entry) => typeof entry === "string" || (allowNull && entry === null));

const isAttemptMap = (value: unknown): boolean =>
	isRecord(value) && Object.values(value).every(isInventoryReconcileAttempt);

const isInventoryReconcileDraft = (value: unknown): value is InventoryReconcileDraft => {
	if (!isRecord(value)) return false;
	const { location, reason, rows } = value;
	return (
		isStringMap(value.addedLineIds) &&
		isStringMap(value.batchCodes, true) &&
		isStringMap(value.hardgoodsLocationIds) &&
		typeof value.grade === "string" &&
		typeof value.why === "string" &&
		typeof value.reasonEdited === "boolean" &&
		(location === null ||
			(isRecord(location) && typeof location.id === "string" && typeof location.name === "string")) &&
		(reason === null || (isRecord(reason) && typeof reason.code === "number" && typeof reason.text === "string")) &&
		isAttemptMap(value.unconfirmedAttempts) &&
		Array.isArray(rows) &&
		rows.every(
			(row) =>
				isRecord(row) &&
				typeof row.id === "string" &&
				typeof row.itemId === "string" &&
				typeof row.locationId === "string" &&
				typeof row.quantity === "number"
		)
	);
};

export default isInventoryReconcileDraft;
